import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { InfoCircle } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { FormRadio, FormRadioGroup } from '../../../../components/radio-group/index';
import { Switch } from '../../../../components/switch/index';
import { useLocale } from '../../../../hooks/index';
import { DaysPerYearOption } from '../../../../types/share-classes.types';
import { onlyNumbers } from '../../../../utils/string-transformers.util';
import { toNumber } from '../../../../utils/to-number.util';
import { ChildrenFormProps } from '../modals.types';
import { FormSchema, fromSchemaStepTwo } from '../modals.validation';

export type StepTwoProps = ChildrenFormProps;

export const StepTwo: FC<StepTwoProps> = ({
  control,
  handleCloseModal,
  prevFormStep,
  nextFormStep,
  setFormData,
  lockMode,
}) => {
  const { messagesLocale } = useLocale();
  const { stepTwo } = useWatch<FormSchema>({ control });
  const { success, error } = fromSchemaStepTwo.safeParse(stepTwo);
  const isValid = success;

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div
          className={twMerge(
            'flex flex-col rounded-md border border-gray-100 bg-white',
            stepTwo?.liquidityPreferences?.enabled && 'bg-gray-50',
          )}
        >
          <div className="flex items-center gap-2 px-4 py-3">
            <Switch
              checked={stepTwo?.liquidityPreferences?.enabled}
              onChange={(state) => {
                if (state) return setFormData('stepTwo.liquidityPreferences.enabled', state);
                setFormData('stepTwo.liquidityPreferences', undefined);
              }}
            />
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="LIQUIDITY_PREFERENCES" />
            </span>
          </div>
          {stepTwo?.liquidityPreferences?.enabled && (
            <div className="flex flex-col gap-3 divide-y-[1px] divide-gray-100 rounded-md bg-white py-3 shadow-sm">
              <div className="flex items-start gap-4 px-4">
                <FormInput
                  control={control}
                  maxDecimalPlaces={2}
                  name="stepTwo.liquidityPreferences.seniority"
                  placeholder={<AppFormattedMessage id="SENIORITY" />}
                />
                <FormInput
                  control={control}
                  maxDecimalPlaces={2}
                  name="stepTwo.liquidityPreferences.multiple"
                  placeholder={<AppFormattedMessage id="MULTIPLY_VALUE" />}
                  regexp={(value) => {
                    if (toNumber(value) > 9.99 && toNumber(value) < 100) {
                      return onlyNumbers(value, 4, true);
                    } else if (toNumber(value) >= 100) {
                      return onlyNumbers(value, 3);
                    }
                    return value;
                  }}
                />
              </div>
              <div className="flex flex-col gap-3 px-4 pt-3">
                <div className="flex min-h-14 items-start gap-4">
                  <div className="flex h-14 w-full items-center gap-2">
                    <Switch
                      checked={stepTwo?.liquidityPreferences?.participating?.enabled}
                      onChange={(state) => {
                        state
                          ? setFormData(
                              'stepTwo.liquidityPreferences.participating.enabled',
                              state,
                              { shouldValidate: true },
                            )
                          : setFormData('stepTwo.liquidityPreferences.participating', undefined);
                      }}
                    />
                    <span className="text-sm font-[450] text-gray-700">
                      {<AppFormattedMessage id="PARTICIPATING" />}
                    </span>
                  </div>
                  {stepTwo?.liquidityPreferences.participating?.enabled && (
                    <FormInput
                      control={control}
                      maxDecimalPlaces={2}
                      name="stepTwo.liquidityPreferences.participating.capValue"
                      placeholder={<AppFormattedMessage id="CAP_VALUE" />}
                      wrapperClassName="w-full"
                    />
                  )}
                </div>
                {stepTwo?.liquidityPreferences.participating?.enabled && (
                  <div className="flex flex-col gap-4 rounded-lg border border-gray-100 p-3">
                    <div className="flex items-start gap-4">
                      <div className="flex h-14 w-full flex-col justify-center">
                        <span className="text-sm font-[450] text-gray-700">
                          <AppFormattedMessage id="YEARLY_INTEREST" />
                        </span>
                        <span className="text-label-sm font-[450] text-gray-400">
                          (<AppFormattedMessage id="NON_COMPOUNDING" />)
                        </span>
                      </div>
                      <FormInput
                        control={control}
                        maxDecimalPlaces={2}
                        name="stepTwo.liquidityPreferences.participating.interest"
                        onChange={(value) => {
                          if (value) return;
                          setFormData(
                            'stepTwo.liquidityPreferences.participating.daysPersYear',
                            undefined,
                          );
                        }}
                        placeholder="%"
                        wrapperClassName="w-full"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="w-full text-sm font-[450] text-gray-700">
                        <AppFormattedMessage id="DAYS_PER_YEAR" />
                      </span>

                      <FormRadioGroup
                        className={'h-11 divide-x divide-gray-200 overflow-hidden'}
                        control={control}
                        name="stepTwo.liquidityPreferences.participating.daysPersYear"
                        wrapperClassName="w-full"
                      >
                        {Object.values(DaysPerYearOption).map((option, i) => (
                          <FormRadio
                            className={twMerge(
                              'w-full bg-gray-50 px-4 py-3 text-[12px] leading-normal text-gray-700 data-[checked]:text-sm data-[checked]:text-gray-25',
                              !stepTwo.liquidityPreferences?.participating?.interest &&
                                'cursor-default bg-gray-50 text-label-md text-gray-700',
                            )}
                            control={control}
                            disabled={!stepTwo.liquidityPreferences?.participating?.interest}
                            key={`${option}_${i}`}
                            name="stepTwo.liquidityPreferences.participating.daysPersYear"
                            value={option}
                          >
                            <span className="w-full text-nowrap text-center font-[450]">
                              {option}
                            </span>
                          </FormRadio>
                        ))}
                      </FormRadioGroup>
                    </div>
                    {stepTwo.liquidityPreferences.participating.daysPersYear &&
                      !stepTwo.liquidityPreferences.participating.interest && (
                        <span className="flex items-center gap-1 text-xs font-[450] text-fireside-600">
                          <InfoCircle />
                          <AppFormattedMessage id="INTEREST_PERCENTAGE_PROMPT" />
                        </span>
                      )}
                    {error?.issues[0].path.includes('multiple') && (
                      <span className="flex items-center gap-1 text-xs font-[450] text-fireside-600">
                        <InfoCircle />
                        {error?.issues[0].message}
                      </span>
                    )}

                    <DropDown
                      answer={[
                        messagesLocale['EARLY_INTEREST_EXPLANATION'],
                        messagesLocale['EXAMPLE'],
                        messagesLocale['EARLY_INTEREST_EXAMPLE'],
                      ]}
                      className="rounded-lg bg-gray-50"
                      question={messagesLocale['WHAT_IS_YEARLY_INTEREST']}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col divide-y-[1px] divide-gray-100 overflow-hidden rounded-lg bg-gray-50">
          <DropDown
            answer={messagesLocale['LIQUIDITY_PREFERENCE_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_ARE_LIQUIDITY_PREFERENCES" />}
          />
          <DropDown
            answer={[messagesLocale['SENIORITY_EXPLANATION'], messagesLocale['SENIORITY_EXAMPLE']]}
            question={<AppFormattedMessage id="WHAT_IS_SENIORITY" />}
          />
          <DropDown
            answer={[messagesLocale['MULTIPLE_EXPLANATION'], messagesLocale['MULTIPLE_EXAMPLE']]}
            question={<AppFormattedMessage id="WHAT_IS_MULTIPLY" />}
          />
          <DropDown
            answer={[
              messagesLocale['PARTICIPATION_EXPLANATION'],
              messagesLocale['PARTICIPATION_EXAMPLE'],
            ]}
            question={<AppFormattedMessage id="WHAT_IS_PARTICIPATING" />}
          />
          <DropDown
            answer={[messagesLocale['CAP_VALUE_EXPLANATION'], messagesLocale['CAP_VALUE_EXAMPLE']]}
            question={<AppFormattedMessage id="WHAT_IS_CAP_VALUE" />}
          />
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {!lockMode && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? (
              <AppFormattedMessage id="UPDATE" />
            ) : stepTwo?.liquidityPreferences?.enabled ? (
              <AppFormattedMessage id="NEXT" />
            ) : (
              <AppFormattedMessage id="SKIP" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
